import React, {useEffect, useRef, useState} from 'react';
import {Badge, Checkbox, Col, Form, Input, message, Popover, Row, Space, Spin, Tabs, Typography} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {FileUploadUtility, WdiFileList} from '@/components/WdiControls/WdiFileUpload';
import TabPane from '@ant-design/pro-card/lib/components/TabPane';
import {FormPageProps, getFormUrlMapping, WdiFormPageTitle} from '@/components/WdiBus/FormPage';
import {
  approvalConfig,
  initApprovalPageContext,
  moreConfig,
  refreshApprovalPageContext,
  ViewEntityProps,
} from '../../components/data';
import {ResponseStatus} from '@/utils/request';
import DataStore from '@/components/GridWidget/DataStore';
import {DataOptions} from '@/services/basic/metadata';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiCardTitle, wdiCore, WdiInput, WdiModalProps, WdiStaffCard,} from '@/components/WdiControls';
import {Enum} from '@/enums/system';
import FormSaInfosApp from '@/pages/sales/sa/components/FormSaInfos';
import FormSsInfosApp from '@/pages/sales/ss/components/FormSsInfos';
import DynamicInformation from '../../components/DynamicInformation';
import {SPWarningApprovalApp} from '@/pages/sales/sp/components/WarningInformation';
import {FormSp} from '@/services/sales/sp';
import {Invoices} from '@/services/sales/financial/invoices';
import {GeneralPaymentDetailsForApprovalPanel} from '../../components/detail';
import {FormInvoiceForApprovalPanel} from '@/pages/sales/sp/components/FormInvoice';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import FormSpSelectApp from '@/pages/sales/sp/components/ViewPrepayment';
import {OaFormApp} from '@/pages/sales/sp/components/OaContractCmpt';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";
import {FormApi} from "@/services/sales";

const ModalAppTypeEnums = {
  None: 0,
  ViewPrepay: 3,
};

const FormColumns = {
  BANK_ACCOUNT_ID: 1,
  SOLD_TO_CODE: 2,
  COMPANY_CODE: 3,
  SUPPLIER_ID: 4,
  OA_FORM_NO: 5,
  REVERSE_PREPAY_DETAIL: 6,
  SOLD_TO_CODE_PAY_TO: 7,
  PAY_TYPE_ID: 9,
  PAY_TO_USER_ID: 10,
  IS_REVERSE_PREPAY: 11,
}

const FieldConfig: Record<number, Set<number>> = {
  [Enum.PaymentMethod.BILL_DEDUCT]: new Set(),
  [Enum.PaymentMethod.FEE_POOL]: new Set(),
  [Enum.PaymentMethod.FG_POOL]: new Set(),
  [Enum.PaymentMethod.BUY_GIVE_POOL]: new Set(),
  [Enum.PaymentMethod.ACCOUNT_DEDUCT]: new Set([FormColumns.SOLD_TO_CODE, FormColumns.SOLD_TO_CODE_PAY_TO]),
  [Enum.PaymentMethod.TRANSFER]: new Set([
    FormColumns.IS_REVERSE_PREPAY, FormColumns.PAY_TYPE_ID, FormColumns.BANK_ACCOUNT_ID, FormColumns.SOLD_TO_CODE,
    FormColumns.COMPANY_CODE]),
  [Enum.PaymentMethod.THIRD_PART]: new Set([
    FormColumns.IS_REVERSE_PREPAY, FormColumns.PAY_TYPE_ID, FormColumns.BANK_ACCOUNT_ID, FormColumns.COMPANY_CODE,
    FormColumns.SUPPLIER_ID, FormColumns.PAY_TO_USER_ID, FormColumns.OA_FORM_NO
  ]),
  [Enum.PaymentMethod.FREE_GOODS]: new Set(),
}

const GeneralPaymentApprovalApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [invoiceDataSource, setInvoiceDataSource] = useState<any[]>([]);
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [warningCount, setWarningCount] = useState<number>(0);
  const [paymentMethodId, setPaymentMethodId] = useState(0);
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false,});
  const [activeKey, setActiveKey] = useState<string>("1");
  const [saForms, setSaForms] = useState<API.Form[]>([]);

  useEffect(() => {
    if (!formRef?.current) {
      return;
    }
    setActiveKey(props.data?.defaultActiveKey ?? "1");
  }, [!!(formRef?.current)])

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApprovalPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;
        if (viewEntity.formSp) {
          formRef.current?.setFieldsValue({
            amountInVat: wdiCore.Number.AmountToString(viewEntity.formSp.amountInVat),
            payToTypeId: viewEntity.formSp.payToTypeName,
            paymentMethodId: viewEntity.formSp.paymentMethodName,
            isReversePrepay: viewEntity.formSp.isReversePrepay,
            payTypeId: viewEntity.formSp.payTypeName,
            bankAccountNo: viewEntity.formSp?.bankAccountNo,
            bankAccountName: viewEntity.formSp?.bankAccountName,
            companyCode: wdiCore.String.Join(viewEntity.formSp.companyCode, viewEntity.formSp.companyName),
            supplierId: wdiCore.String.Join(viewEntity.formSp.supplierCode, viewEntity.formSp.supplierName),
            payToUserId: wdiCore.String.GenerateLabelDescription(wdiCore.String.Join(viewEntity.formSp.payToUserNo, viewEntity.formSp.payToUserName), viewEntity.formSp.payToUserTitle),
            oaFormNo: {
              oaFormNo: viewEntity.formSp.oaFormNo,
              oaFormName: viewEntity.formSp.oaFormName,
              url: viewEntity.formSp.oaFormUrl,
            },
            fileInfo: FileUploadUtility.parse(viewEntity.formSp.fileName, viewEntity.formSp.filePath),
            remark: viewEntity.formSp.remark,
            reversePrepayDetail: viewEntity.formSp?.reversePrepayDetail,
          });
          if (viewEntity.formSp?.reimburseMaterial) {
            formRef.current?.setFieldsValue({
              reimburseMaterials: viewEntity.formSp?.reimburseMaterial.split(','),
            });
          }
          FormSp.Buttons.countOfWarning(viewEntity?.formSp?.formSpId || 0).then(response => {
            setWarningCount(response.data);
          });
        }

        if (viewEntity.formSpDetails) {
          const formSpDetail: API.FormSpDetailApprovalView = viewEntity.formSpDetails[0];
          formRef.current?.setFieldsValue({
            vatTypeId: formSpDetail.vatTypeName,
            voucherNo: formSpDetail.voucherNo,
            soldToCode: wdiCore.String.CodeOrName(formSpDetail.soldToCode, formSpDetail.soldToName),
            soldToCodePayTo: wdiCore.String.CodeOrName(formSpDetail.soldToCodePayTo, formSpDetail.soldToNamePayTo),
          });
          setDataSource(DataStore.init(viewEntity.formSpDetails));

          if (viewEntity.form?.pageGroupId == Enum.PageGroup.ACTIVITY_BATCH_PAYMENT) {
            const formIds = new Set(viewEntity.formSpDetails.map(item => (item.formSaId)));
            const forms = [];
            for (const formId of formIds) {
              const form = await FormApi.get(Number(formId));
              forms.push(form.data);
            }
            setSaForms(forms);
          }
        }
        if (viewEntity.formSp?.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE) {
          Invoices.getForPayment(viewEntity.formSp?.formSpId || 0).then(responseResult => {
            setInvoiceDataSource(DataStore.init(responseResult.data));
          })
        }
        setPaymentMethodId(Number(viewEntity.paymentMethodId));
        setEntity(viewEntity);
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = `活动核销单【${entity.form?.formNo}】`;
      DataOptions.search({typeGroup: 3, value: entity.form?.statusId}).then(response => {
        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message).then();
          return;
        }
        if (response.data && response.data.length > 0) {
          if (entity.form?.statusId != Enum.FormStatus.SCRAP && entity.form?.exportStatus == Enum.ExportStatus.EXPORT_SUCCESS) {
            title += `，已导出`;
          } else {
            title += `，${response.data[0].name}`;
          }
          if (props.setPageTitle) props.setPageTitle(
            <WdiFormPageTitle
              title={title}
              onClose={props.onClose}
              callback={props.callback}
              form={entity?.form}
              useCaseId={Enum.UseCase.SP}
              moreConfig={moreConfig}
              approvalConfig={approvalConfig}/>
          );
        }
      });
    }
  }, [props.setPageTitle, entity]);

  const onDetailsChange = async (data: any[]) => {
    setDataSource(data);
    const refreshEntity = await refreshApprovalPageContext(Number(entity?.form?.formId));
    formRef.current?.setFieldsValue({
      amountInVat: wdiCore.Number.AmountToString(refreshEntity.formSp.amountInVat),
    });
    setInvoiceDataSource(DataStore.init(refreshEntity.invoices))
  };

  return (
    <>
      {modalProps.visible && modalProps.type == ModalAppTypeEnums.ViewPrepay && (
        <FormSpSelectApp
          {...modalProps}
          onSave={() => {
            setModalProps({visible: false});
          }}
          onClose={() => {
            setModalProps({visible: false});
          }}
        />
      )}
      <WdiStaffCard formId={entity?.form?.formId}/>
      <Tabs activeKey={activeKey} onTabClick={(key, _) => setActiveKey(key)} size="large">
        <TabPane tab={<span>核销信息</span>} key="1">
          <ProForm formRef={formRef} submitter={{
            render: () => {
              return <></>;
            },
          }}>
            {entity?.formSaId && entity?.pageGroupId != Enum.PageGroup.ACTIVITY_BATCH_PAYMENT && (
              <FormSaInfosApp formRef={formRef} defaultCollapsed={true} formSaId={entity?.formSaId}/>
            )}
            {entity?.formSsId && (
              <FormSsInfosApp formRef={formRef} defaultCollapsed={true} formSsId={entity?.formSsId}/>
            )}
            <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title="活动核销单信息"/>} collapsed={true}>
              <Row gutter={[16, 0]}>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="amountInVat" label="本次核销含税总金额">
                    <WdiInputNumber digits={2} readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="vatTypeId" label="可抵扣税率类型(普票为0)">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="paymentMethodId" label="支付方式">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="payToTypeId" label="支付给">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                {FieldConfig[paymentMethodId]?.has(FormColumns.IS_REVERSE_PREPAY) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="isReversePrepay" label="是否冲销预付" valuePropName="checked">
                      <Checkbox disabled/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.IS_REVERSE_PREPAY) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="reversePrepayDetail" label="冲销的预付单">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.PAY_TYPE_ID) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="payTypeId" label="支付类型">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.BANK_ACCOUNT_ID) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="bankAccountNo" label="银行账号">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.BANK_ACCOUNT_ID) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="bankAccountName" label="户名">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.SOLD_TO_CODE) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="soldToCode" label="客户">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.SOLD_TO_CODE_PAY_TO) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="soldToCodePayTo" label="付款客户">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.COMPANY_CODE) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="companyCode" label="公司">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.SUPPLIER_ID) && entity?.formSp?.supplierId && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="supplierId" label="供应商">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.PAY_TO_USER_ID) && entity?.formSp?.payToUserId && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="payToUserId" label="员工">
                      <WdiInput readOnly/>
                    </Form.Item>
                  </Col>
                )}
                {FieldConfig[paymentMethodId]?.has(FormColumns.OA_FORM_NO) && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="oaFormNo" label="OA合同号">
                      <OaFormApp/>
                    </Form.Item>
                  </Col>
                )}
                <Col className="gutter-row" span={4}>
                  <Form.Item name="voucherNo" label="外部系统单号">
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                {entity?.formSp?.pageGroupId == Enum.PageGroup.ACTIVITY_BATCH_PAYMENT && (
                  <Col className="gutter-row" span={4}>
                    <Form.Item name="formSaNo" label="活动单号">
                      <Popover content={() => {
                        return (
                          <Space direction="vertical">
                            {saForms.map((item) => (
                              <Typography.Link key={item.formId} onClick={() => {
                                window.open(getFormUrlMapping(item.pageTypeId, item.formTypeId) + `?formId=${item.formId}&pageTypeId=${item.pageTypeId}&formTypeId=${item.formTypeId}`);
                              }}>
                                {item.formNo}
                              </Typography.Link>
                            ))}
                          </Space>
                        );
                      }}>
                        <Input readOnly value={saForms.map((item) => item.formNo).join(',')}/>
                      </Popover>
                    </Form.Item>
                  </Col>
                )}
                <Col className="gutter-row" span={24}>
                  <Form.Item name="reimburseMaterials" label="核销支持文件">
                    <Checkbox.Group
                      disabled
                      options={(entity?.reimburseMaterials ?? []).map((item) => ({
                        label: item.name,
                        value: String(item.value),
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 24]}>
                <Col className="gutter-row" span={16}>
                  <Form.Item name="remark" label="核销备注">
                    <WdiEditor readOnly={true}/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={8}>
                  <Form.Item
                    name="fileInfo"
                    label="核销附件"
                    valuePropName="fileList"
                    getValueFromEvent={(e: any) => {
                      return e.fileList || [];
                    }}
                  >
                    <WdiFileList/>
                  </Form.Item>
                </Col>
              </Row>
            </WdiPanel>
          </ProForm>
          {entity?.form && dataSource && (
            <GeneralPaymentDetailsForApprovalPanel
              data={{
                ecFieldConfigs: entity?.ecFieldConfigs,
                allowViewSfa: entity?.allowViewSfa,
                formSp: entity?.formSp,
                form: entity?.form,
                paymentMethod: entity?.paymentMethod,
              }}
              dataSource={dataSource}
              onChange={onDetailsChange}
              onClose={props.onClose}
            />
          )}
          {entity?.formSp?.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE && (
            <FormInvoiceForApprovalPanel dataSource={invoiceDataSource}/>
          )}
        </TabPane>
        <TabPane
          tab={
            <>
              <span>警示信息</span>
              <Badge
                count={warningCount}
                style={{
                  color: 'black',
                  boxShadow: 'none',
                  background: 'none',
                  top: '-20px',
                  right: '-15px',
                  position: 'absolute',
                }}
              ></Badge>
            </>
          }
          key="3"
        >
          <SPWarningApprovalApp formId={entity?.form?.formId}/>
        </TabPane>
        <TabPane tab={<span>动态信息</span>} key="4">
          {entity?.form ? (
            <DynamicInformation
              formId={entity?.form?.formId}
              formTypeId={entity?.form?.formTypeId}
              pageTypeId={entity?.form?.pageTypeId}
              useCaseId={Enum.UseCase.SP}
            />
          ) : (
            <Spin/>
          )}
        </TabPane>
      </Tabs>
    </>
  );
};

export default GeneralPaymentApprovalApp;
